import React from "react";
import "./viewFranchiseManagerHistory.css";

const formatDateTime = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return "—";
    }
};

const humanizeKey = (key = "") =>
    key
        .split(".")
        .map((part) =>
            part.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
        )
        .join(" › ");

const isImageUrl = (val) =>
    typeof val === "string" && /^https?:\/\/.+\.(jpg|jpeg|png|webp|gif)(\?.*)?$/i.test(val);

const formatValue = (val) => {
    if (val === null || val === undefined || val === "") return "—";
    if (Array.isArray(val)) return val.length ? val.join(", ") : "—";
    if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}T/.test(val)) {
        return formatDateTime(val);
    }
    return String(val);
};

const renderChangeValue = (val, variant) => {
    if (Array.isArray(val)) {
        const images = val.filter(isImageUrl);
        if (images.length) {
            return (
                <div className="vfmh-change-thumbs">
                    {images.map((src, i) => (
                        <a href={src} target="_blank" rel="noreferrer" key={i}>
                            <img
                                src={src}
                                alt="attachment"
                                className="vfmh-change-thumb"
                                onError={(e) => {
                                    e.target.style.display = "none";
                                    e.target.nextSibling?.classList?.remove("vfmh-hidden");
                                }}
                            />
                            <span className="vfmh-change-thumb-broken vfmh-hidden">📎 View file</span>
                        </a>
                    ))}
                </div>
            );
        }
    }

    if (isImageUrl(val)) {
        return (
            <a href={val} target="_blank" rel="noreferrer">
                <img
                    src={val}
                    alt="attachment"
                    className="vfmh-change-thumb"
                    onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextSibling?.classList?.remove("vfmh-hidden");
                    }}
                />
                <span className="vfmh-change-thumb-broken vfmh-hidden">📎 View file</span>
            </a>
        );
    }

    return <span className={`vfmh-change-${variant}`}>{formatValue(val)}</span>;
};

// ---- Access Control diffing ----
const ACCESS_FLAGS = ["read", "write", "update", "delete"];

const diffAccessControl = (oldVal, newVal) => {
    const oldSections = Array.isArray(oldVal) ? oldVal : [];
    const newSections = Array.isArray(newVal) ? newVal : [];

    const oldMap = new Map(oldSections.map((s) => [s.section, s]));
    const newMap = new Map(newSections.map((s) => [s.section, s]));
    const sectionNames = new Set([...oldMap.keys(), ...newMap.keys()]);

    const diffs = [];

    sectionNames.forEach((sectionName) => {
        const oldSection = oldMap.get(sectionName);
        const newSection = newMap.get(sectionName);

        ACCESS_FLAGS.forEach((flag) => {
            const from = !!oldSection?.[flag];
            const to = !!newSection?.[flag];
            if (from !== to) {
                diffs.push({ section: sectionName, sub_section: null, flag, from, to });
            }
        });

        const oldSubMap = new Map((oldSection?.sub_access || []).map((s) => [s.sub_section, s]));
        const newSubMap = new Map((newSection?.sub_access || []).map((s) => [s.sub_section, s]));
        const subNames = new Set([...oldSubMap.keys(), ...newSubMap.keys()]);

        subNames.forEach((subName) => {
            const oldSub = oldSubMap.get(subName);
            const newSub = newSubMap.get(subName);
            ACCESS_FLAGS.forEach((flag) => {
                const from = !!oldSub?.[flag];
                const to = !!newSub?.[flag];
                if (from !== to) {
                    diffs.push({ section: sectionName, sub_section: subName, flag, from, to });
                }
            });
        });
    });

    return diffs;
};

const renderAccessControlChange = (oldVal, newVal) => {
    const diffs = diffAccessControl(oldVal, newVal);

    if (diffs.length === 0) {
        return <span className="vfmh-acl-nochange">No permission changes</span>;
    }

    return (
        <div className="vfmh-acl-diff">
            {diffs.map((d, i) => (
                <div className="vfmh-acl-diff-row" key={i}>
                    <span className="vfmh-acl-diff-path">
                        {humanizeKey(d.section)}
                        {d.sub_section && (
                            <>
                                <span className="vfmh-acl-diff-arrow-sub">›</span>
                                {humanizeKey(d.sub_section)}
                            </>
                        )}
                        <span className="vfmh-acl-diff-flag">· {humanizeKey(d.flag)}</span>
                    </span>

                    <span className="vfmh-acl-diff-states">
                        <span className={`vfmh-acl-diff-dot vfmh-acl-diff-dot--${d.from ? "on" : "off"}`} />
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="5" y1="12" x2="19" y2="12" />
                            <polyline points="12 5 19 12 12 19" />
                        </svg>
                        <span className={`vfmh-acl-diff-dot vfmh-acl-diff-dot--${d.to ? "on" : "off"}`} />
                        <span className={`vfmh-acl-diff-label vfmh-acl-diff-label--${d.to ? "on" : "off"}`}>
                            {d.to ? "Enabled" : "Disabled"}
                        </span>
                    </span>
                </div>
            ))}
        </div>
    );
};

// ---- Array diffing (project, locality, city, etc.) ----
const diffArrayValue = (oldVal, newVal) => {
    const oldArr = Array.isArray(oldVal) ? oldVal.map(String) : [];
    const newArr = Array.isArray(newVal) ? newVal.map(String) : [];
    const removed = oldArr.filter((v) => !newArr.includes(v));
    const added = newArr.filter((v) => !oldArr.includes(v));
    const unchanged = oldArr.filter((v) => newArr.includes(v));
    return { removed, added, unchanged };
};

const renderArrayDiff = (oldVal, newVal) => {
    const { removed, added, unchanged } = diffArrayValue(oldVal, newVal);

    if (removed.length === 0 && added.length === 0) {
        return <span className="vfmh-acl-nochange">No changes</span>;
    }

    return (
        <div className="vfmh-array-diff">
            {unchanged.length > 0 && (
                <div className="vfmh-array-diff-group">
                    {unchanged.map((v, i) => (
                        <span className="vfmh-chip vfmh-chip--neutral" key={`u-${i}`}>{v}</span>
                    ))}
                </div>
            )}
            {removed.length > 0 && (
                <div className="vfmh-array-diff-group">
                    {removed.map((v, i) => (
                        <span className="vfmh-chip vfmh-chip--removed" key={`r-${i}`}>{v}</span>
                    ))}
                </div>
            )}
            {added.length > 0 && (
                <div className="vfmh-array-diff-group">
                    {added.map((v, i) => (
                        <span className="vfmh-chip vfmh-chip--added" key={`a-${i}`}>{v}</span>
                    ))}
                </div>
            )}
        </div>
    );
};

const ARRAY_DIFF_FIELDS = ["project", "locality", "city"];

const ViewFranchiseManagerHistory = ({ open, onClose, history = [] }) => {
    if (!open) return null;

    return (
        <div className="vfmh-overlay" onClick={onClose}>
            <div className="vfmh-modal" onClick={(e) => e.stopPropagation()}>
                <div className="vfmh-header">
                    <h3>Change History</h3>
                    <button className="vfmh-close" onClick={onClose} type="button" aria-label="Close">
                        &times;
                    </button>
                </div>

                <div className="vfmh-body">
                    {history.length === 0 ? (
                        <p className="vfmh-empty">No history available.</p>
                    ) : (
                        [...history]
                            .sort((a, b) => new Date(b.changed_at) - new Date(a.changed_at))
                            .map((entry) => (
                                <div className="vfmh-entry" key={entry._id}>
                                    <div className="vfmh-entry-meta">
                                        <span className="vfmh-role">{humanizeKey(entry.changed_by_role)}</span>
                                        <span className="vfmh-date">{formatDateTime(entry.changed_at)}</span>
                                    </div>

                                    <div className="vfmh-changes">
                                        {(entry.changes || []).map((change) => (
                                            <div
                                                className={`vfmh-change-row ${
                                                    change.field === "access_control" ||
                                                    ARRAY_DIFF_FIELDS.includes(change.field)
                                                        ? "vfmh-change-row--wide"
                                                        : ""
                                                }`}
                                                key={change._id}
                                            >
                                                <span className="vfmh-change-field">{humanizeKey(change.field)}</span>

                                                {change.field === "access_control" ? (
                                                    renderAccessControlChange(change.old_value, change.new_value)
                                                ) : ARRAY_DIFF_FIELDS.includes(change.field) ? (
                                                    renderArrayDiff(change.old_value, change.new_value)
                                                ) : (
                                                    <>
                                                        {renderChangeValue(change.old_value, "old")}
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <line x1="5" y1="12" x2="19" y2="12" />
                                                            <polyline points="12 5 19 12 12 19" />
                                                        </svg>
                                                        {renderChangeValue(change.new_value, "new")}
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    {entry.remarks && <p className="vfmh-remarks">{entry.remarks}</p>}
                                </div>
                            ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default ViewFranchiseManagerHistory;